import type { Visitor } from './types'

// localStorage wrapper for widget session state.
//
// Keys live on the customer's page origin. Private mode or blocked
// storage just means a fresh conversation on every load; every access is
// wrapped so that never throws into the widget.

// Two widgets (or two client ids during a migration) must not share a
// session, so the keys carry the client id. `configure` runs before
// anything is read.
const LEGACY_SESSION = 'airys.widget.session'
const LEGACY_CONVERSATION_ID = 'airys.widget.conversationId'
const VISITOR = 'airys.widget.visitor'
let KEY_SESSION = LEGACY_SESSION
let KEY_CONVERSATION_ID = LEGACY_CONVERSATION_ID
let KEY_VISITOR = VISITOR

/**
 * Scopes stored state to one widget client, and carries over anything the
 * previous unscoped version left behind so a visitor mid-conversation
 * isn't dropped by the upgrade.
 */
export function configureStorage(clientId: string) {
  if (!clientId) return
  KEY_SESSION = `${LEGACY_SESSION}.${clientId}`
  KEY_CONVERSATION_ID = `${LEGACY_CONVERSATION_ID}.${clientId}`
  KEY_VISITOR = `${VISITOR}.${clientId}`
  for (const [legacy, key] of [
    [LEGACY_SESSION, KEY_SESSION],
    [LEGACY_CONVERSATION_ID, KEY_CONVERSATION_ID],
  ] as const) {
    const carried = safeRead(legacy)
    if (carried !== null && safeRead(key) === null) safeWrite(key, carried)
    safeDelete(legacy)
  }
}

export interface StoredSession {
  token: string
  expiresAt: string // RFC3339
  absoluteExpiresAt: string
  verified: boolean
}

function safeRead(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function safeWrite(key: string, value: string) {
  try { localStorage.setItem(key, value) } catch { /* quota / private mode */ }
}
function safeDelete(key: string) {
  try { localStorage.removeItem(key) } catch { /* ignore */ }
}

// Only the 7-day ceiling is checked locally. The 1-hour sliding window is
// the server's call: a request past it answers 401 session_expired and
// session.ts refreshes.
export function readSession(): StoredSession | null {
  const raw = safeRead(KEY_SESSION)
  if (!raw) return null
  try {
    const s = JSON.parse(raw) as StoredSession
    if (typeof s.token !== 'string' || !s.token) return null
    if (new Date(s.absoluteExpiresAt).getTime() < Date.now()) return null
    return s
  } catch {
    return null
  }
}

export function writeSession(s: StoredSession) {
  safeWrite(KEY_SESSION, JSON.stringify(s))
}

// Clears only the token. The conversation id stays so a re-minted session
// can pick the same conversation up; callers that mean to drop the
// conversation call clearConversationId() as well.
export function clearSession() {
  safeDelete(KEY_SESSION)
}

/**
 * The name and email the visitor typed into the pre-chat form. Kept so a
 * reload doesn't ask them again — it is what they said about themselves,
 * never proof of who they are.
 */
export function readVisitor(): Visitor | null {
  const raw = safeRead(KEY_VISITOR)
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as Partial<Visitor>
    return v && typeof v.name === 'string' && typeof v.email === 'string'
      ? { name: v.name, email: v.email }
      : null
  } catch {
    return null
  }
}
export function writeVisitor(v: Visitor) { safeWrite(KEY_VISITOR, JSON.stringify(v)) }
export function clearVisitor() { safeDelete(KEY_VISITOR) }

export function readConversationId(): string | null { return safeRead(KEY_CONVERSATION_ID) }
export function writeConversationId(id: string) { safeWrite(KEY_CONVERSATION_ID, id) }
export function clearConversationId() { safeDelete(KEY_CONVERSATION_ID) }
